import { Injectable } from '@angular/core';

export interface FsCsvMember {
  firstName: string;
  lastName: string;
}

/** One group and its members — the unit both CSV layouts are built from. */
export interface FsCsvGroup {
  name: string;
  members: FsCsvMember[];
}

export type FsCsvFormat = 'flat' | 'grouped';

export interface FsCsvOptions {
  format: FsCsvFormat;
  /** Filename stem, without extension — `download()` appends `_members_<format>.csv`. */
  filename: string;
  /** The flat layout's third column header: 'Team' for Organizations, 'Group' for Activity Groups. */
  groupColumnHeader: string;
}

/**
 * The group-list CSV export, shared by Organizations and Activity Groups.
 *
 * The two layouts are deliberately asymmetric — that asymmetry is the roster's original export
 * format, not an oversight:
 *   flat    — quoted header row, then one row per member with their group appended
 *   grouped — NO header row; group name on its own row, members beneath, blank line between groups
 *
 * Hosts adapt their own data into `FsCsvGroup[]`; this service knows nothing about rosters,
 * memberships, report targets or filename slugs.
 */
@Injectable({ providedIn: 'root' })
export class FsCsvExportService {
  /** Pure: builds the CSV text. Separated from `download()` so the row rules can be asserted. */
  build(groups: FsCsvGroup[], opts: FsCsvOptions): string {
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const list = groups ?? [];
    const rows: string[] = [];

    if (opts.format === 'grouped') {
      list.forEach((g, i) => {
        if (i > 0) { rows.push(''); }          // blank line between groups
        rows.push(esc(g.name));                 // group name on its own row
        (g.members ?? []).forEach(m => rows.push([esc(m.firstName), esc(m.lastName)].join(',')));
      });
    } else {
      rows.push([esc('First Name'), esc('Last Name'), esc(opts.groupColumnHeader)].join(','));
      list.forEach(g => {
        (g.members ?? []).forEach(m =>
          rows.push([esc(m.firstName), esc(m.lastName), esc(g.name)].join(',')));
      });
    }

    return rows.join('\n');
  }

  /** Builds and hands the file to the browser. */
  download(groups: FsCsvGroup[], opts: FsCsvOptions): void {
    const blob = new Blob([this.build(groups, opts)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${opts.filename}_members_${opts.format}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
